import React, { Component } from 'react'
import { BrowserRouter, Route, Switch, matchPath } from 'react-router-dom'
import axios from 'axios'
import Cookies from 'universal-cookie'
import './App.scss'
import { observer, inject } from 'mobx-react'
import { Text, Spinner } from '@fluentui/react'
import moment from 'moment'
import { setUser } from '@sentry/browser'

import './scss/customTheme.css'
import 'primereact/resources/primereact.min.css'
import 'primeicons/primeicons.css'

import { initializeIcons } from '@fluentui/font-icons-mdl2'
import { RefreshModal } from './components/RefreshModal/RefreshModal'
import {
  initMixpanel,
  mixpanelCustomEvent,
  mixpanelIdentifyUser,
  mixpanelSwitchToDemoEnvironment,
  mixpanelSwitchToLiveEnvironment
} from './util/mixpanelHelper'
import routes from './routes'
import Loading from './components/Loading'
import AppInner from './AppInner'
import { SUBSCRIPTION_STATUS } from './constants/tenantConstants'
import { USER_ROLE } from './constants/userRoleConstants'
initializeIcons()

const cookies = new Cookies()

const signOut = async () => {
  const cookies = new Cookies()
  const cookieOptions = {}

  if (process.env.NODE_ENV === 'production') {
    cookieOptions.domain = '.teamflect.com'
  }
  cookies.remove('x-region-admin', cookieOptions)

  await axios.get('/api/authorize/clear-cookie-admin', {
    withCredentials: true
  })
}

axios.defaults.baseURL = process.env.REACT_APP_API_URI
axios.defaults.headers.common['x-region'] = cookies.get('x-region-admin')
axios.interceptors.response.use(
  function (response) {
    return response
  },
  async function (error) {
    if (error?.response?.data?.error === 'INVALID_TOKEN') {
      localStorage.clear()
      await signOut()
      window.location.href = '/login'
      return Promise.reject(error)
    }
    return Promise.reject(error)
  }
)

// Pages
const Maintenance = React.lazy(() => import('./views/Maintenance/Maintenance'))

function getQueryParams() {
  let qs = window.location.search.slice(1)
  let items = qs.split('&')
  let params = {}
  for (let i = 0; i < items.length; i++) {
    let [key, value] = items[i].split('=')
    params[key] = decodeURIComponent(value)
  }
  return params
}

const App = inject(
  'userStore',
  'appStore'
)(
  observer(
    class App extends Component {
      constructor(props, context) {
        super(props, context)
        this.state = {
          loginStatus: 0,
          isChecking: false,
          demoMode: false,
          isIdTokenFetching: false
        }
      }

      isDemo() {
        const searchParams = new URLSearchParams(window.location.search)
        return searchParams.get('demo') === 'true'
      }

      setSelectedDemoUser() {
        const searchParams = new URLSearchParams(window.location.search)
        const demoUser = searchParams.get('user')

        if (demoUser) {
          localStorage.setItem('user', demoUser)
          localStorage.setItem('demo', 'true')
        }
      }

      async componentDidMount() {
        const demoMode = this.isDemo()
        if (demoMode) {
          this.setSelectedDemoUser()
        }
        window.addEventListener('focus', () => {
          const exp = localStorage.getItem('exp')
          if (exp) {
            const now = new Date().getTime()
            if (now > exp) {
              localStorage.removeItem('exp')
              this.setState({ isRefreshing: true })
            }
          }
        })
        initMixpanel()
        this.handleUpdateScreenSize()
        if (window.location.hash) {
          localStorage.removeItem('redirectTo')
        }
        let redirectTo = localStorage.getItem('redirectTo')
        if (redirectTo) {
          localStorage.removeItem('redirectTo')
          window.location.hash = redirectTo
        }
        if (this.isMaintenance()) {
          this.handleMaintenanceRedirection()
          return
        }
        let idToken

        try {
          if (process.env.REACT_APP_ENVIRONMENT === 'slot') {
            idToken = cookies.get('idTokenAdmin')
          } else {
            idToken = await this.getIdTokenAdmin()
          }
        } catch (error) {
          console.log('error ' + error)
        }
        if (idToken !== undefined && idToken !== 'error') {
          this.checkWebUser(idToken)
        } else {
          let params = getQueryParams()
          idToken = params.token
          if (idToken !== undefined && idToken !== 'error') {
            // when we redirected from app query params includes token and region we cant access httponly cookies in slot env so we are setting client side cookies
            if (idToken && process.env.REACT_APP_ENVIRONMENT === 'slot') {
              cookies.set('idTokenAdmin', idToken)
              cookies.set('x-region-admin', params.region)
              axios.defaults.headers.common['x-region'] = params.region
            }

            this.checkWebUser(idToken)
          } else {
          }
        }
      }

      isMaintenance = () => {
        const isMaintenanceByPassed = cookies.get('maintenance-bypass') == 'true'
        const isMaintenance = process.env.REACT_APP_IS_MAINTENANCE === 'true' && !isMaintenanceByPassed
        if (isMaintenance) {
          this.setState({ loginStatus: 'MAINTENANCE' })
        }
        return isMaintenance
      }

      handleMaintenanceRedirection = () => {
        const location = window.location
        const isRouteMaintenance = location.pathname === '/maintenance'
        const isMaintenance = process.env.REACT_APP_IS_MAINTENANCE === 'true'

        const isMaintenanceByPassed = cookies.get('maintenance-bypass') == 'true'

        // If it is the maintenance page, do not redirect
        if (isRouteMaintenance || isMaintenanceByPassed || !isMaintenance) return false

        let route = routes.find(r => matchPath(location.pathname, { path: r.path, exact: true }))

        if (route) {
          mixpanelCustomEvent('Maintenance Page Redirection', {
            page: route.name
          })
        }

        // redirect to maintenance page
        window.location.href = '/maintenance'
        return true
      }

      getIdTokenAdmin = async () => {
        this.setState({ isIdTokenFetching: true })
        let res = null
        try {
          res = await axios.get('/api/authorize/getIdTokenAdmin', {
            withCredentials: true
          })
        } catch (e) {
          if (this.isMaintenance()) {
            this.setState({ loginStatus: 'MAINTENANCE' })
          } else {
            this.setState({ loginStatus: 0 })
          }
        }
        this.setState({ isIdTokenFetching: false })

        return res?.data
      }
      checkWebUser = idToken => {
        this.setState({ isChecking: true })

        const demoMode = localStorage.getItem('demo') === 'true'
        const demoUser = localStorage.getItem('user')

        axios
          .post('/api/authorize/checkWebUser', {
            idToken: idToken,
            adminLogin: true,
            demoMode: demoMode,
            selectedDemoUser: demoUser
          })
          .then(response => {
            this.setState({ isChecking: false })
            if (response.data.message !== 'ok') {
              this.setState({ loginStatus: 1 })
              //this.context.router.history.push('/login')
            } else {
              localStorage.setItem('exp', response.data.exp)
              this.props.userStore.setUser(response.data.userData)

              if (!demoMode) {
                setUser({ email: response.data.userData.UPN })
              }

              if ([1, 2, 3, 4].includes(response.data.userData.role)) {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + response.data.accessToken
                this.setState({ loginStatus: 2 })

                mixpanelIdentifyUser(response.data.userData, response.false)

                if (demoMode) {
                  axios.defaults.headers.get['Pragma'] = 'no-cache'
                  axios.defaults.baseURL = process.env.REACT_APP_DEMO_URI
                  axios.defaults.headers.common['demo-mode'] = 'true'
                  axios.defaults.headers.common['selected-demo-user'] = response.data.userData.displayName
                  mixpanelSwitchToDemoEnvironment()
                }

                const userData = response.data.userData
                if (!demoMode) {
                  var INTERCOM_APP_ID = 'dfpxiwhb'
                  let subscriptionStatus = 'free'
                  if (userData.tenant?.trialSubscription?.isExpired === false) {
                    subscriptionStatus = 'trial'
                  } else if (userData.tenant.subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE) {
                    subscriptionStatus = 'paid'
                  }

                  const region = cookies.get('x-region-admin')

                  // @ts-ignore
                  window.Intercom('boot', {
                    app_id: INTERCOM_APP_ID,
                    name: userData.givenName ? userData.givenName : '',
                    user_id: userData._id,
                    email: userData.mail,
                    'Job Title': userData.jobTitle,
                    'Subscription Status': subscriptionStatus,
                    'Admin Role': userData.role === USER_ROLE.GlobalAdministrator ? 'admin' : 'user',
                    created_at: moment(userData.createdAt).unix(),
                    'CSM Responsible': userData.tenant ? userData.tenant.csmDetails?.responsibleCSM : '',
                    'Sales Responsible': userData.tenant
                      ? userData.tenant.subscriptionDetails?.responsibleSalesPerson
                      : '',
                    phone: userData.mobilePhone ? userData.mobilePhone : userData.businessPhones[0],
                    'Graph User Count': userData.tenant ? userData.tenant.graphTotalUserCount : '',
                    'Licensed User Count': userData.tenant ? userData.tenant.licensedUserCount : '',
                    'Birthday Recognition Active': userData?.tenant?.settings?.enableBirthdayRecognition ?? false,
                    company: {
                      company_id: userData.tenant._id,
                      name: userData.tenant.displayName
                    },
                    region: region
                  })
                  mixpanelSwitchToLiveEnvironment()
                }

                if (window.Appcues) {
                  window.Appcues.identify(
                    userData._id, // unique, required
                    {
                      createdAt: parseInt((new Date(userData.createdAt).getTime() / 1000).toFixed(0)), // Unix timestamp of user signup date
                      planTier: userData.tenant.subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE ? 'paid' : 'free', // Current user’s plan tier
                      role: userData.role > 1 ? 'admin' : 'user', // Current user’s role or permissions
                      firstName: userData.displayName, // current user's first name
                      companyName: userData.tenant.displayName // Current user’s company name
                    }
                  )
                }
              } else {
                this.setState({ loginStatus: 3 })
              }
            }
          })
          .catch(error => {
            console.log(error)
            this.setState({ isChecking: false })
            if (this.isMaintenance()) {
              this.setState({ loginStatus: 'MAINTENANCE' })
            } else {
              this.setState({ loginStatus: 1 })
            }
          })
      }

      click = () => {
        localStorage.setItem('redirectTo', window.location.hash)
        let url = process.env.REACT_APP_APP_URI + '/authorize'
        const isDemo = this.isDemo()
        isDemo && this.setSelectedDemoUser()
        localStorage.setItem('demo', isDemo ? 'true' : 'false')
        // let redirectState = encodeURIComponent(parsed.redirectTo) ? '&state=' + encodeURIComponent(parsed.redirectTo) : '';
        window.location.href =
          'https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=' +
          process.env.REACT_APP_CLIENT_ID +
          '&redirect_uri=' +
          url +
          '&response_type=code&prompt=select_account&scope=' +
          process.env.REACT_APP_APP_BASIC_SCOPES +
          '&state=' +
          (isDemo ? 'demoadmin' : 'admin')
      }

      loginPage = () => {
        const isDark = true
        const styleObj = {
          cursor: 'pointer',
          marginBottom: 56
        }
        return (
          <div
            className={'backlogin'}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100vw',
              height: '100vh'
            }}
          >
            <div
              style={{
                backgroundImage: 'url("https://teamblee.blob.core.windows.net/teamflect/B17.webp")',
                position: 'absolute',
                zIndex: -1,
                width: '100vw',
                height: '100vh',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover'
              }}
            />
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyItems: 'center',
                alignItems: 'center',
                alignContent: 'center',
                zIndex: 1000000
              }}
            >
              <img style={{ marginBottom: 8 }} src='../../assets/img/logo-filled.png' height='60' alt='logo' />
              <Text variant={'large'} style={{ fontWeight: 700 }}>
                Admin Center
              </Text>
            </div>
            {/*
                      <DefaultButton text='Get Started' onClick={this.click} style={{maxWidth: '160px', marginTop: '15px', marginBottom: '35px', alignSelf: 'center' , borderRadius: '11.5px', border: 'solid 0px', backgroundColor: '#db5461', color: 'white' }} />
                      */}
            {this.state.isChecking || this.state.isIdTokenFetching ? (
              <Spinner
                style={{
                  marginTop: '25px',
                  marginBottom: '35px',
                  alignSelf: 'center'
                }}
              />
            ) : (
              <div
                style={{
                  marginTop: '25px',
                  marginBottom: '35px',
                  alignSelf: 'center'
                }}
              >
                <svg xmlns='http://www.w3.org/2000/svg' width='104' height='41' style={styleObj} onClick={this.click}>
                  <path fill={isDark ? '#2f2f2f' : '#ffffff'} d='M0 0h104v41H0z' />
                  {!isDark && <path fill='#8c8c8c' d='M103 1v39H1V1h102m1-1H0v41h104V0z' />}
                  <path fill='#f25022' d='M13 11h9v9h-9z' />
                  <path fill='#00a4ef' d='M13 21h9v9h-9z' />
                  <path fill='#7fba00' d='M23 11h9v9h-9z' />
                  <path fill='#ffb900' d='M23 21h9v9h-9z' />
                  <path
                    fill={isDark ? '#ffffff' : '#5e5e5e'}
                    d='M45.812 25.082v-1.794a2.77 2.77 0 0 0 .573.4 4.484 4.484 0 0 0 .706.3 5.486 5.486 0 0 0 .745.187 3.954 3.954 0 0 0 .687.065 2.928 2.928 0 0 0 1.634-.365 1.2 1.2 0 0 0 .537-1.062 1.167 1.167 0 0 0-.178-.649 1.939 1.939 0 0 0-.5-.5 5.412 5.412 0 0 0-.757-.435q-.435-.209-.932-.436-.533-.285-.994-.578a4.285 4.285 0 0 1-.8-.648 2.724 2.724 0 0 1-.533-.8 2.6 2.6 0 0 1-.194-1.047 2.416 2.416 0 0 1 .333-1.285 2.794 2.794 0 0 1 .877-.9 4.019 4.019 0 0 1 1.239-.528 5.906 5.906 0 0 1 1.418-.172 5.692 5.692 0 0 1 2.4.374v1.721a3.817 3.817 0 0 0-2.295-.645 4.093 4.093 0 0 0-.771.074 2.335 2.335 0 0 0-.687.241 1.5 1.5 0 0 0-.494.433 1.06 1.06 0 0 0-.189.637 1.221 1.221 0 0 0 .145.608 1.573 1.573 0 0 0 .428.468 4.321 4.321 0 0 0 .688.414c.27.134.584.28.939.436q.548.285 1.034.6a4.881 4.881 0 0 1 .856.7 3.075 3.075 0 0 1 .585.846 2.493 2.493 0 0 1 .215 1.058 2.625 2.625 0 0 1-.322 1.348 2.584 2.584 0 0 1-.866.892 3.786 3.786 0 0 1-1.254.5 6.959 6.959 0 0 1-1.5.155c-.176 0-.392-.014-.647-.04s-.518-.067-.786-.117a7.75 7.75 0 0 1-.76-.187 2.373 2.373 0 0 1-.58-.269zM55.109 16.426a1.021 1.021 0 0 1-.713-.272.891.891 0 0 1-.3-.688.917.917 0 0 1 .3-.7 1.009 1.009 0 0 1 .713-.278 1.041 1.041 0 0 1 .732.278.915.915 0 0 1 .3.7.9.9 0 0 1-.3.678 1.035 1.035 0 0 1-.732.282zm.841 9.074h-1.7V18h1.7zM64.979 24.9q0 4.131-4.146 4.131a6.166 6.166 0 0 1-2.551-.491v-1.554a4.712 4.712 0 0 0 2.332.7 2.341 2.341 0 0 0 2.668-2.628v-.818h-.029a2.938 2.938 0 0 1-4.733.436 4.046 4.046 0 0 1-.837-2.684 4.738 4.738 0 0 1 .9-3.04 2.988 2.988 0 0 1 2.471-1.128 2.38 2.38 0 0 1 2.2 1.216h.029V18h1.7zM63.3 22.064v-.973a1.91 1.91 0 0 0-.523-1.352 1.71 1.71 0 0 0-1.3-.559 1.789 1.789 0 0 0-1.51.714 3.223 3.223 0 0 0-.545 2 2.78 2.78 0 0 0 .523 1.769 1.675 1.675 0 0 0 1.385.662 1.8 1.8 0 0 0 1.426-.632 2.4 2.4 0 0 0 .544-1.629zM73.853 25.5h-1.7v-4.227q0-2.1-1.483-2.1a1.616 1.616 0 0 0-1.279.582 2.167 2.167 0 0 0-.505 1.469V25.5h-1.7V18h1.7v1.245h.029a2.669 2.669 0 0 1 2.428-1.421 2.257 2.257 0 0 1 1.863.795 3.57 3.57 0 0 1 .644 2.3zM80.892 16.426a1.017 1.017 0 0 1-.713-.272.889.889 0 0 1-.3-.688.915.915 0 0 1 .3-.7 1 1 0 0 1 .713-.278 1.038 1.038 0 0 1 .731.278.915.915 0 0 1 .3.7.9.9 0 0 1-.3.678 1.033 1.033 0 0 1-.731.282zm.84 9.074h-1.7V18h1.7zM90.614 25.5h-1.7v-4.227q0-2.1-1.483-2.1a1.62 1.62 0 0 0-1.28.582 2.167 2.167 0 0 0-.5 1.469V25.5h-1.7V18h1.7v1.245h.03a2.668 2.668 0 0 1 2.427-1.421 2.258 2.258 0 0 1 1.864.795 3.576 3.576 0 0 1 .643 2.3z'
                  />
                </svg>
                <img
                  style={{ position: 'absolute', right: '20px', top: '20px', maxHeight: '56px' }}
                  src='https://teamblee.blob.core.windows.net/teamflect/aicpa-soc.png'
                />
                <img
                  style={{ position: 'absolute', right: '96px', top: '20px', maxHeight: '56px' }}
                  src='https://teamblee.blob.core.windows.net/teamflect/Group_48096710.png'
                />
                <img
                  style={{ position: 'absolute', right: '139.67px', top: '5px', maxHeight: '80px' }}
                  src='https://teamblee.blob.core.windows.net/teamflect/microsoft_partner_dark.png'
                />
                <img
                  style={{ position: 'absolute', width: '50px', right: '20vw', top: '22vh' }}
                  src='https://teamblee.blob.core.windows.net/teamflect/Group_48096705.png'
                />
                <img
                  style={{ position: 'absolute', height: '50px', bottom: '35vh', left: '13vw' }}
                  src='https://teamblee.blob.core.windows.net/teamflect/Group_48096706.png'
                />
              </div>
            )}

            <Text variant={'medium'}>{this.state.textt}</Text>
            <div style={{ position: 'absolute', bottom: '5px' }}>
              <Text variant={'small'} style={{ fontStyle: 'italic' }}>
                © {new Date().getFullYear()} Teamflect App - All Rights Reserved
              </Text>
            </div>
          </div>
        )
      }

      handleUpdateScreenSize = () => {
        this.props.appStore.updateScreenSize()
        window.addEventListener('resize', () => {
          this.props.appStore.updateScreenSize()
        })
      }

      render() {
        if (this.state.loginStatus === 'MAINTENANCE') {
          return (
            <>
              {this.state.isRefreshing && <RefreshModal />}
              <BrowserRouter>
                <React.Suspense fallback={<Loading />}>
                  <Switch>
                    <Route path='/maintenance' component={Maintenance} />
                  </Switch>
                </React.Suspense>
              </BrowserRouter>
            </>
          )
        }
        if (this.state.loginStatus === 2) {
          return <AppInner isRefreshing={this.state.isRefreshing} />
        } else if (this.state.loginStatus === 3) {
          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'center',
                alignSelf: 'center',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh'
              }}
            >
              <Text
                variant='medium'
                style={{
                  textAlign: 'center',
                  alignSelf: 'center',
                  fontSize: 18,
                  fontWeight: 550
                }}
              >
                You are not authorized to see this page
              </Text>
              <div
                style={{
                  marginTop: '25px',
                  marginBottom: '35px',
                  alignSelf: 'center'
                }}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='104'
                  height='41'
                  style={{
                    cursor: 'pointer'
                  }}
                  onClick={this.click}
                >
                  <path fill={'#2f2f2f'} d='M0 0h104v41H0z' />
                  <path fill='#f25022' d='M13 11h9v9h-9z' />
                  <path fill='#00a4ef' d='M13 21h9v9h-9z' />
                  <path fill='#7fba00' d='M23 11h9v9h-9z' />
                  <path fill='#ffb900' d='M23 21h9v9h-9z' />
                  <path
                    fill={'#ffffff'}
                    d='M45.812 25.082v-1.794a2.77 2.77 0 0 0 .573.4 4.484 4.484 0 0 0 .706.3 5.486 5.486 0 0 0 .745.187 3.954 3.954 0 0 0 .687.065 2.928 2.928 0 0 0 1.634-.365 1.2 1.2 0 0 0 .537-1.062 1.167 1.167 0 0 0-.178-.649 1.939 1.939 0 0 0-.5-.5 5.412 5.412 0 0 0-.757-.435q-.435-.209-.932-.436-.533-.285-.994-.578a4.285 4.285 0 0 1-.8-.648 2.724 2.724 0 0 1-.533-.8 2.6 2.6 0 0 1-.194-1.047 2.416 2.416 0 0 1 .333-1.285 2.794 2.794 0 0 1 .877-.9 4.019 4.019 0 0 1 1.239-.528 5.906 5.906 0 0 1 1.418-.172 5.692 5.692 0 0 1 2.4.374v1.721a3.817 3.817 0 0 0-2.295-.645 4.093 4.093 0 0 0-.771.074 2.335 2.335 0 0 0-.687.241 1.5 1.5 0 0 0-.494.433 1.06 1.06 0 0 0-.189.637 1.221 1.221 0 0 0 .145.608 1.573 1.573 0 0 0 .428.468 4.321 4.321 0 0 0 .688.414c.27.134.584.28.939.436q.548.285 1.034.6a4.881 4.881 0 0 1 .856.7 3.075 3.075 0 0 1 .585.846 2.493 2.493 0 0 1 .215 1.058 2.625 2.625 0 0 1-.322 1.348 2.584 2.584 0 0 1-.866.892 3.786 3.786 0 0 1-1.254.5 6.959 6.959 0 0 1-1.5.155c-.176 0-.392-.014-.647-.04s-.518-.067-.786-.117a7.75 7.75 0 0 1-.76-.187 2.373 2.373 0 0 1-.58-.269zM55.109 16.426a1.021 1.021 0 0 1-.713-.272.891.891 0 0 1-.3-.688.917.917 0 0 1 .3-.7 1.009 1.009 0 0 1 .713-.278 1.041 1.041 0 0 1 .732.278.915.915 0 0 1 .3.7.9.9 0 0 1-.3.678 1.035 1.035 0 0 1-.732.282zm.841 9.074h-1.7V18h1.7zM64.979 24.9q0 4.131-4.146 4.131a6.166 6.166 0 0 1-2.551-.491v-1.554a4.712 4.712 0 0 0 2.332.7 2.341 2.341 0 0 0 2.668-2.628v-.818h-.029a2.938 2.938 0 0 1-4.733.436 4.046 4.046 0 0 1-.837-2.684 4.738 4.738 0 0 1 .9-3.04 2.988 2.988 0 0 1 2.471-1.128 2.38 2.38 0 0 1 2.2 1.216h.029V18h1.7zM63.3 22.064v-.973a1.91 1.91 0 0 0-.523-1.352 1.71 1.71 0 0 0-1.3-.559 1.789 1.789 0 0 0-1.51.714 3.223 3.223 0 0 0-.545 2 2.78 2.78 0 0 0 .523 1.769 1.675 1.675 0 0 0 1.385.662 1.8 1.8 0 0 0 1.426-.632 2.4 2.4 0 0 0 .544-1.629zM73.853 25.5h-1.7v-4.227q0-2.1-1.483-2.1a1.616 1.616 0 0 0-1.279.582 2.167 2.167 0 0 0-.505 1.469V25.5h-1.7V18h1.7v1.245h.029a2.669 2.669 0 0 1 2.428-1.421 2.257 2.257 0 0 1 1.863.795 3.57 3.57 0 0 1 .644 2.3zM80.892 16.426a1.017 1.017 0 0 1-.713-.272.889.889 0 0 1-.3-.688.915.915 0 0 1 .3-.7 1 1 0 0 1 .713-.278 1.038 1.038 0 0 1 .731.278.915.915 0 0 1 .3.7.9.9 0 0 1-.3.678 1.033 1.033 0 0 1-.731.282zm.84 9.074h-1.7V18h1.7zM90.614 25.5h-1.7v-4.227q0-2.1-1.483-2.1a1.62 1.62 0 0 0-1.28.582 2.167 2.167 0 0 0-.5 1.469V25.5h-1.7V18h1.7v1.245h.03a2.668 2.668 0 0 1 2.427-1.421 2.258 2.258 0 0 1 1.864.795 3.576 3.576 0 0 1 .643 2.3z'
                  />
                </svg>
              </div>
            </div>
          )
        } else {
          return this.loginPage()
        }
      }
    }
  )
)

export default App
